import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Wrench, ScanLine, ScanSearch, Camera, Radar, CircleDot, Eye, Car,
  Compass, Sun, Route, Printer, AlertCircle, Building2, Phone, Mail, Globe,
  MessageSquare, X, CheckCircle2, Send, Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────
// Icon map — keys match backend PRICING_CATALOG[k].icon
// ─────────────────────────────────────────────────────────────
const ICONS = {
  wrench: Wrench,
  scan: ScanLine,
  "scan-check": ScanSearch,
  camera: Camera,
  radar: Radar,
  circle: CircleDot,
  eye: Eye,
  car: Car,
  steering: Compass,
  sun: Sun,
  route: Route,
};

const CATEGORY_STYLES = {
  scan: { ring: "border-blue-500/40", accent: "bg-blue-500/10 text-blue-300", dot: "bg-blue-400" },
  adas: { ring: "border-purple-500/40", accent: "bg-purple-500/10 text-purple-300", dot: "bg-purple-400" },
  reset: { ring: "border-amber-500/40", accent: "bg-amber-500/10 text-amber-300", dot: "bg-amber-400" },
  lighting: { ring: "border-yellow-500/40", accent: "bg-yellow-500/10 text-yellow-300", dot: "bg-yellow-400" },
  verify: { ring: "border-emerald-500/40", accent: "bg-emerald-500/10 text-emerald-300", dot: "bg-emerald-400" },
  labor: { ring: "border-slate-600", accent: "bg-slate-700/40 text-slate-300", dot: "bg-slate-400" },
};

export default function PublicPricing() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem("truline_pricing_lang") || "en");
  const [expanded, setExpanded] = useState({}); // { key: true }
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/public/pricing/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Not found"))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    localStorage.setItem("truline_pricing_lang", lang);
  }, [lang]);

  const grouped = useMemo(() => {
    if (!data) return [];
    const byCat = {};
    (data.items || []).forEach((it) => {
      byCat[it.category] = byCat[it.category] || [];
      byCat[it.category].push(it);
    });
    return (data.categories || []).map((c) => ({
      ...c,
      items: byCat[c.key] || [],
    })).filter((c) => c.items.length > 0);
  }, [data]);

  const t = lang === "pt" ? (data?.disclaimer?.pt) : (data?.disclaimer?.en);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] text-slate-200 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#030712] text-slate-200 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-rose-400 mx-auto mb-4" />
          <h1 className="font-display text-2xl text-rose-300 mb-2">
            {lang === "pt" ? "Link não encontrado" : "Link not found"}
          </h1>
          <p className="text-sm text-slate-400">
            {error === "This pricing link has been disabled by the shop"
              ? (lang === "pt" ? "Este link foi desativado pela oficina." : "This link has been disabled by the shop.")
              : (lang === "pt" ? "O link expirou ou está incorreto. Peça uma nova URL para o vendedor." : "The link is invalid or expired. Ask the sales rep for a new URL.")}
          </p>
        </div>
      </div>
    );
  }

  const co = data.company || {};

  return (
    <div className="min-h-screen bg-[#030712] text-slate-200">
      {/* Header */}
      <header className="sticky top-0 z-20 backdrop-blur-md bg-[#030712]/85 border-b border-slate-800 print:static print:bg-white print:text-slate-900 print:border-slate-300">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {co.logo_url ? (
              <img src={co.logo_url} alt="" className="w-11 h-11 rounded-lg object-cover" />
            ) : (
              <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-black text-xl print:from-blue-600 print:to-blue-800">T</div>
            )}
            <div className="leading-tight">
              <div className="font-display text-lg font-semibold">{co.name}</div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-blue-400 font-bold print:text-slate-600">
                {lang === "pt" ? "TABELA DE PREÇOS" : "PRICING SHEET"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Language toggle */}
            <div className="inline-flex bg-slate-900/60 border border-slate-800 rounded-lg p-0.5 print:hidden" data-testid="lang-switcher">
              <button
                onClick={() => setLang("en")}
                data-testid="lang-en-btn"
                className={`px-3 py-1 text-xs font-bold rounded transition ${lang === "en" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"}`}
              >
                EN
              </button>
              <button
                onClick={() => setLang("pt")}
                data-testid="lang-pt-btn"
                className={`px-3 py-1 text-xs font-bold rounded transition ${lang === "pt" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-200"}`}
              >
                PT
              </button>
            </div>
            <button
              onClick={() => window.print()}
              data-testid="print-btn"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-slate-200 border border-slate-800 hover:border-slate-600 rounded-md px-3 py-1.5 print:hidden"
            >
              <Printer className="w-3.5 h-3.5" /> {lang === "pt" ? "Imprimir" : "Print"}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-6 pt-10 pb-6 print:pt-6">
        <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 font-bold mb-3 print:text-amber-700">
          {data.table_name}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.1] print:text-slate-900">
          {lang === "pt"
            ? "Preços de calibração ADAS"
            : "ADAS Calibration Pricing"}
        </h1>
        <p className="mt-3 text-base text-slate-400 max-w-2xl leading-relaxed print:text-slate-600">
          {lang === "pt"
            ? "Nossa tabela pública para vendedores e parceiros. Clique em qualquer item para ver quando é necessário e o que está incluído."
            : "Our public rate sheet for sales reps and partners. Tap any item to see when it's needed and what's included."}
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-6 text-xs text-slate-500 print:text-slate-700">
          {co.phone && (
            <span className="inline-flex items-center gap-1.5"><Phone className="w-3 h-3" /> {co.phone}</span>
          )}
          {co.email && (
            <span className="inline-flex items-center gap-1.5"><Mail className="w-3 h-3" /> {co.email}</span>
          )}
          {co.website && (
            <a href={co.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-blue-400 hover:text-blue-300">
              <Globe className="w-3 h-3" /> {co.website.replace(/^https?:\/\//, "")}
            </a>
          )}
        </div>
      </div>

      {/* Categories */}
      <div className="max-w-5xl mx-auto px-6 pb-16 space-y-10">
        {grouped.map((cat) => (
          <section key={cat.key} data-testid={`pricing-cat-${cat.key}`}>
            <div className="flex items-center gap-2 mb-4">
              <span className={`w-2.5 h-2.5 rounded-full ${CATEGORY_STYLES[cat.key]?.dot || "bg-slate-500"}`} />
              <h2 className="font-display text-2xl font-semibold tracking-tight print:text-slate-900">
                {lang === "pt" ? cat.pt_name : cat.en_name}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4 print:grid-cols-1">
              {cat.items.map((item) => {
                const Icon = ICONS[item.icon] || Wrench;
                const cs = CATEGORY_STYLES[item.category] || CATEGORY_STYLES.labor;
                const info = lang === "pt" ? item.pt : item.en;
                const isOpen = !!expanded[item.key];
                return (
                  <div
                    key={item.key}
                    data-testid={`pricing-item-${item.key}`}
                    className={`rounded-xl border ${cs.ring} bg-[#0B1120] p-5 transition hover:border-opacity-80 print:bg-white print:border-slate-300`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`shrink-0 w-10 h-10 rounded-lg ${cs.accent} border ${cs.ring} flex items-center justify-center print:bg-slate-100 print:text-slate-800 print:border-slate-300`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-display text-base font-semibold text-slate-100 truncate print:text-slate-900">
                              {info.name}
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5 print:text-slate-500">
                              {info.unit}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-mono text-2xl font-bold text-blue-300 leading-none print:text-blue-700">
                              ${item.price.toFixed(2)}
                            </div>
                            <div className="text-[9px] text-slate-600 mt-1 uppercase tracking-wider print:text-slate-500">USD</div>
                          </div>
                        </div>
                        <p className="text-sm text-slate-300 mt-2 leading-relaxed print:text-slate-700">{info.short}</p>

                        {/* Expand toggle */}
                        <button
                          onClick={() => setExpanded((s) => ({ ...s, [item.key]: !s[item.key] }))}
                          data-testid={`expand-${item.key}`}
                          className="text-[11px] uppercase tracking-wider font-bold text-blue-400 hover:text-blue-300 mt-3 print:hidden"
                        >
                          {isOpen
                            ? (lang === "pt" ? "Ocultar detalhes" : "Hide details")
                            : (lang === "pt" ? "Ver detalhes" : "Show details")}
                        </button>
                        {(isOpen || typeof window !== "undefined" && window.matchMedia && window.matchMedia("print").matches) && (
                          <div className="mt-3 space-y-3 border-t border-slate-800 pt-3 print:border-slate-300 print:block">
                            <p className="text-sm text-slate-300 leading-relaxed print:text-slate-700">{info.long}</p>
                            <div className="rounded-md bg-slate-900/50 border border-slate-800 p-3 print:bg-slate-50 print:border-slate-300">
                              <div className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold mb-1 print:text-emerald-700">
                                {lang === "pt" ? "Quando aplicar" : "When applied"}
                              </div>
                              <div className="text-xs text-slate-300 leading-relaxed print:text-slate-700">{info.when}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        {/* Disclaimer */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 text-xs text-slate-400 leading-relaxed print:bg-slate-50 print:border-slate-300 print:text-slate-600">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
            {lang === "pt" ? "Aviso importante" : "Disclaimer"}
          </div>
          {t}
        </div>

        {/* Footer */}
        <div className="text-center pt-6 border-t border-slate-800 print:border-slate-300">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold print:text-slate-500">
            <Building2 className="w-3 h-3" /> {lang === "pt" ? "Documento gerado por" : "Generated by"} TrueLine Calibration Platform
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: white !important; color: #0f172a !important; }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
}

// Note: The "Request a quote" floating button + modal were removed from this
// public pricing page in Iteration 29. The auto-quote experience now lives
// inside the per-body-shop portal (`/submit/:token` → PublicSubmit.jsx),
// where each shop's agreed discount is applied to the estimate.

// ─────────────────────────────────────────────────────────────
// Quote Request Modal — captures a lead and posts to the CRM
// ─────────────────────────────────────────────────────────────
const T = {
  en: {
    title: "Request a quote",
    subtitle: "Fill in a few details — a sales rep will call you within 15 minutes during business hours.",
    company: "Business name",
    contact: "Your name",
    phone: "Phone",
    email: "Email (optional)",
    year: "Year",
    make: "Make",
    model: "Model",
    vin: "VIN (optional)",
    services: "Services needed",
    servicesPh: "e.g. front camera + radar after windshield replacement",
    message: "Message (optional)",
    messagePh: "Anything else we should know?",
    type: "Business type",
    types: { body_shop: "Body Shop", glass_shop: "Glass Shop", dealership: "Dealership", fleet: "Fleet", other: "Other" },
    submit: "Send request",
    sending: "Sending...",
    successTitle: "Request received!",
    successMsg: "Thanks — a sales rep will call you shortly.",
    closeBtn: "Close",
    required: "required",
    error: "Something went wrong. Please try again or call us directly.",
  },
  pt: {
    title: "Solicitar cotação",
    subtitle: "Preencha alguns dados — um vendedor liga em até 15 minutos no horário comercial.",
    company: "Nome da empresa",
    contact: "Seu nome",
    phone: "Telefone",
    email: "E-mail (opcional)",
    year: "Ano",
    make: "Marca",
    model: "Modelo",
    vin: "VIN (opcional)",
    services: "Serviços necessários",
    servicesPh: "ex: câmera frontal + radar após troca de para-brisa",
    message: "Mensagem (opcional)",
    messagePh: "Algo mais que devemos saber?",
    type: "Tipo de negócio",
    types: { body_shop: "Body Shop / Funilaria", glass_shop: "Vidraceiro", dealership: "Concessionária", fleet: "Frota", other: "Outro" },
    submit: "Enviar solicitação",
    sending: "Enviando...",
    successTitle: "Recebemos!",
    successMsg: "Obrigado! Um vendedor entrará em contato em breve.",
    closeBtn: "Fechar",
    required: "obrigatório",
    error: "Algo deu errado. Tente de novo ou nos ligue diretamente.",
  },
};

function QuoteRequestModal({ token, lang, onClose }) {
  const tr = T[lang] || T.en;
  const [form, setForm] = useState({
    company_name: "", contact_name: "", phone: "", email: "",
    vehicle_year: "", vehicle_make: "", vehicle_model: "", vin: "",
    services_needed: "", message: "", type: "body_shop",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [errText, setErrText] = useState(null);
  const [quote, setQuote] = useState(null);          // auto-quote result
  const [quoting, setQuoting] = useState(false);
  const quoteTimerRef = React.useRef(null);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Debounced auto-quote fetch — triggers whenever services or VIN changes
  React.useEffect(() => {
    if (quoteTimerRef.current) clearTimeout(quoteTimerRef.current);
    // Need at least a few chars in services OR a valid VIN
    const hasServices = (form.services_needed || "").trim().length > 4;
    const hasVin = (form.vin || "").length === 17;
    if (!hasServices && !hasVin) {
      setQuote(null);
      return;
    }
    quoteTimerRef.current = setTimeout(async () => {
      setQuoting(true);
      try {
        const r = await axios.post(`${API}/api/public/pricing/auto-quote`, {
          share_token: token,
          vin: form.vin.trim() || null,
          vehicle_year: form.vehicle_year ? parseInt(form.vehicle_year, 10) : null,
          vehicle_make: form.vehicle_make.trim() || null,
          vehicle_model: form.vehicle_model.trim() || null,
          services_needed: form.services_needed.trim() || null,
          language: lang,
        });
        setQuote(r.data);
      } catch (e) {
        setQuote(null);
      } finally {
        setQuoting(false);
      }
    }, 700);
    return () => { if (quoteTimerRef.current) clearTimeout(quoteTimerRef.current); };
  }, [form.services_needed, form.vin, form.vehicle_year, form.vehicle_make, form.vehicle_model, lang, token]);

  const submit = async () => {
    setErrText(null);
    if (!form.company_name.trim() || !form.contact_name.trim() || !form.phone.trim()) {
      setErrText(tr.error);
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/public/pricing/request-quote`, {
        share_token: token,
        company_name: form.company_name.trim(),
        contact_name: form.contact_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        vin: form.vin.trim() || null,
        vehicle_year: form.vehicle_year ? parseInt(form.vehicle_year, 10) : null,
        vehicle_make: form.vehicle_make.trim() || null,
        vehicle_model: form.vehicle_model.trim() || null,
        services_needed: form.services_needed.trim() || null,
        message: form.message.trim() || null,
        language: lang,
        type: form.type,
      });
      setSuccess(r.data);
    } catch (e) {
      setErrText(e?.response?.data?.detail || tr.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 print:hidden" data-testid="quote-modal">
      <div className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl bg-[#0B1120] border border-slate-800 shadow-2xl">
        <button
          onClick={onClose}
          data-testid="quote-close-btn"
          className="absolute top-4 right-4 text-slate-500 hover:text-slate-200 p-1"
        >
          <X className="w-5 h-5" />
        </button>

        {success ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 border-2 border-emerald-500 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="font-display text-2xl text-emerald-300 mb-2" data-testid="quote-success">{tr.successTitle}</h2>
            <p className="text-sm text-slate-300 leading-relaxed max-w-xs mx-auto">
              {lang === "pt" ? (success.message_pt || tr.successMsg) : (success.message_en || tr.successMsg)}
            </p>
            <button
              onClick={onClose}
              className="mt-6 px-5 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-medium"
              data-testid="quote-success-close"
            >
              {tr.closeBtn}
            </button>
          </div>
        ) : (
          <div className="p-6 sm:p-8">
            <div className="mb-6">
              <div className="text-[10px] uppercase tracking-[0.24em] text-emerald-400 font-bold mb-1.5">
                {lang === "pt" ? "TABELA DE PREÇOS" : "PRICING PAGE"}
              </div>
              <h2 className="font-display text-2xl font-semibold" data-testid="quote-title">{tr.title}</h2>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{tr.subtitle}</p>
            </div>

            <div className="space-y-3">
              <Field label={tr.company} required tr={tr}>
                <input
                  value={form.company_name}
                  onChange={(e) => set("company_name", e.target.value)}
                  data-testid="quote-company"
                  className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  placeholder={lang === "pt" ? "Ex: Auto Body ABC" : "e.g. ABC Auto Body"}
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label={tr.contact} required tr={tr}>
                  <input
                    value={form.contact_name}
                    onChange={(e) => set("contact_name", e.target.value)}
                    data-testid="quote-contact"
                    className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  />
                </Field>
                <Field label={tr.phone} required tr={tr}>
                  <input
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    data-testid="quote-phone"
                    className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    placeholder="+1 555-000-0000"
                  />
                </Field>
              </div>

              <Field label={tr.email} tr={tr}>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  data-testid="quote-email"
                  className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                />
              </Field>

              <Field label={tr.type} tr={tr}>
                <select
                  value={form.type}
                  onChange={(e) => set("type", e.target.value)}
                  data-testid="quote-type"
                  className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                >
                  {Object.entries(tr.types).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </Field>

              <div className="grid grid-cols-3 gap-3">
                <Field label={tr.year} tr={tr}>
                  <input
                    inputMode="numeric"
                    value={form.vehicle_year}
                    onChange={(e) => set("vehicle_year", e.target.value)}
                    data-testid="quote-year"
                    className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    placeholder="2022"
                  />
                </Field>
                <Field label={tr.make} tr={tr}>
                  <input
                    value={form.vehicle_make}
                    onChange={(e) => set("vehicle_make", e.target.value)}
                    data-testid="quote-make"
                    className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    placeholder="Honda"
                  />
                </Field>
                <Field label={tr.model} tr={tr}>
                  <input
                    value={form.vehicle_model}
                    onChange={(e) => set("vehicle_model", e.target.value)}
                    data-testid="quote-model"
                    className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    placeholder="CR-V"
                  />
                </Field>
              </div>

              <Field label={tr.vin} tr={tr}>
                <input
                  value={form.vin}
                  onChange={(e) => set("vin", e.target.value.toUpperCase())}
                  data-testid="quote-vin"
                  maxLength={17}
                  className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm font-mono uppercase focus:border-emerald-500 focus:outline-none"
                  placeholder="1HGCM82633A123456"
                />
              </Field>

              <Field label={tr.services} tr={tr}>
                <textarea
                  value={form.services_needed}
                  onChange={(e) => set("services_needed", e.target.value)}
                  data-testid="quote-services"
                  rows={2}
                  className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none resize-none"
                  placeholder={tr.servicesPh}
                />
              </Field>

              <Field label={tr.message} tr={tr}>
                <textarea
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  data-testid="quote-message"
                  rows={2}
                  className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none resize-none"
                  placeholder={tr.messagePh}
                />
              </Field>

              {/* Real-time auto-quote panel */}
              {(quoting || quote) && (
                <div
                  className="rounded-xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-amber-600/5 p-4"
                  data-testid="auto-quote-panel"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300 font-bold flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" />
                      {lang === "pt" ? "COTAÇÃO INSTANTÂNEA" : "INSTANT QUOTE"}
                    </div>
                    {quoting && <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-300" />}
                  </div>
                  {quote && quote.operations && quote.operations.length > 0 ? (
                    <>
                      <div className="space-y-1.5 mb-3">
                        {quote.operations.map((op) => (
                          <div key={op.key} className="flex items-center justify-between text-xs" data-testid={`quote-op-${op.key}`}>
                            <span className="text-slate-200 truncate flex-1 min-w-0">{op.name}</span>
                            <span className="font-mono text-blue-300 font-semibold ml-2">${op.price.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="border-t border-amber-500/20 pt-2 flex items-center justify-between">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                            {lang === "pt" ? "TOTAL ESTIMADO" : "ESTIMATED TOTAL"}
                          </div>
                          <div className="text-[10px] text-emerald-400 mt-0.5">
                            {lang === "pt" ? "Confiança" : "Confidence"}: {Math.round((quote.confidence_avg || 0) * 100)}%
                          </div>
                        </div>
                        <div className="text-2xl font-mono font-bold text-amber-300" data-testid="quote-total">
                          ${quote.total.toFixed(2)}
                        </div>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-2 italic">
                        {lang === "pt" ? quote.message_pt : quote.message_en}
                      </div>
                    </>
                  ) : quote && quote.operations && quote.operations.length === 0 ? (
                    <div className="text-xs text-slate-400">
                      {lang === "pt" ? quote.message_pt : quote.message_en}
                    </div>
                  ) : null}
                </div>
              )}

              {errText && (
                <div className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-300 text-xs p-2.5 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{errText}</span>
                </div>
              )}

              <button
                onClick={submit}
                disabled={submitting}
                data-testid="quote-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-semibold text-sm transition"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {tr.sending}</>
                ) : (
                  <><Send className="w-4 h-4" /> {tr.submit}</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children, tr }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
        {label}{required && <span className="text-emerald-400 ml-1">({tr.required})</span>}
      </div>
      {children}
    </label>
  );
}
